import { UsersApi, NotificationsApi, ApiClient } from '../generated/src';
import API_CONFIG from '../config/apiConfig';

/**
 * Service for managing user profile and notification preference data
 * Handles fetching and updating user profile information via Fire Bridge
 */
class UserService {
  constructor() {
    this.apiClient = null;
    this.usersApi = null;
    this.notificationsApi = null;
    this.tokenGetter = null;
  }

  /**
   * Initialize the API client with the given base URL
   * @param {string} baseUrl - The Fire Bridge server base URL
   * @param {Function} getTokenFn - Function that returns the current authentication token
   * @throws {Error} If the ApiClient fails to construct with the provided base URL
   */
  initialize(baseUrl, getTokenFn) {
    try {
      this.apiClient = new ApiClient(baseUrl);
      this.apiClient.timeout = API_CONFIG.TIMEOUT;

      if (getTokenFn && typeof getTokenFn === 'function') {
        this.tokenGetter = getTokenFn;
      }

      this.usersApi = new UsersApi(this.apiClient);
      this.notificationsApi = new NotificationsApi(this.apiClient);

      console.log(`UserService initialized with base URL: ${baseUrl}`);
    } catch (error) {
      console.error('Failed to initialize UserService:', error);
      throw error;
    }
  }

  /**
   * Update the authentication token before making API calls.
   * @throws {Error} If the service is not initialized or no token is available.
   */
  updateToken() {
    if (!this.tokenGetter || !this.apiClient) {
      throw new Error('UserService not initialized. Call initialize() first.');
    }

    const currentToken = this.tokenGetter();
    if (!currentToken) {
      throw new Error('No authentication token available.');
    }

    this.apiClient.authentications['BearerAuth'].accessToken = currentToken;
  }

  /**
   * Fetch a user's profile by ID
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} The user's profile data (UserResponse)
   */
  async getUser(userId) {
    if (!this.usersApi) {
      throw new Error('UserService not initialized. Call initialize() first.');
    }

    try {
      this.updateToken();
    } catch (error) {
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      this.usersApi.getUser(userId, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }

  /**
   * Update a user's profile with partial data
   * @param {string} userId - The user ID
   * @param {Object} userUpdate - Partial user data to update (UserUpdate)
   * @returns {Promise<Object>} The updated user profile data (UserResponse)
   */
  async updateUser(userId, userUpdate) {
    if (!this.usersApi) {
      throw new Error('UserService not initialized. Call initialize() first.');
    }

    try {
      this.updateToken();
    } catch (error) {
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      this.usersApi.updateUser(userId, userUpdate, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }

  /**
   * Fetch a user's notification preferences
   * @param {string} userId - The user ID
   * @returns {Promise<Object>} The user's notification preferences (UserNotificationPreferences)
   */
  async getUserNotificationPreferences(userId) {
    if (!this.notificationsApi) {
      throw new Error('UserService not initialized. Call initialize() first.');
    }

    try {
      this.updateToken();
    } catch (error) {
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      this.notificationsApi.getUserNotificationPreferences(userId, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }

  /**
   * Partially update a user's notification preferences
   * @param {string} userId - The user ID
   * @param {Object} patch - Partial notification preferences to update (UserNotificationPreferencesPatch)
   * @returns {Promise<Object>} The updated notification preferences (UserNotificationPreferences)
   */
  async updateUserNotificationPreferences(userId, patch) {
    if (!this.notificationsApi) {
      throw new Error('UserService not initialized. Call initialize() first.');
    }

    try {
      this.updateToken();
    } catch (error) {
      return Promise.reject(error);
    }

    return new Promise((resolve, reject) => {
      this.notificationsApi.updateUserNotificationPreferences(userId, patch, (error, data) => {
        if (error) {
          reject(error);
        } else {
          resolve(data);
        }
      });
    });
  }
}

export const userService = new UserService();
export default userService;
